import React, { useState, useEffect, useRef } from "react";
import { Box, VStack, HStack, Input, Text, IconButton, Image, Tooltip, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, useDisclosure } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import send_icon from '../Assets/icons/send-icon.svg';
import { fetchExpertChatHistory, sendExpertChatMessage, closeTicket } from "../utils/zendesk.api";

const ExpertChatBot = ({ email, conversationId, onClose, isMobile, vehicle_id }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [ticketId, setTicketId] = useState(null);
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const ignoreInitialMessagesRef = useRef(true);
  const initialMessageTimeoutRef = useRef(null);
  const connectionTimeRef = useRef(null);
  const initialMessageCountRef = useRef(0);
  const { isOpen, onOpen, onClose: onCloseModal } = useDisclosure();

  const pickDisplayText = (rawText) => {
    if (!rawText) return null;
    const firstBreak = rawText.indexOf("\n");
    const shortened = firstBreak === -1 ? rawText : rawText.slice(0, firstBreak);
    return shortened.trim() || null;
  };

  const normalizeChatMessage = (msg, extra = {}) => {
    if (!msg) return null;

    const textCandidate = msg.text || msg.message || msg.content;
    const text = pickDisplayText(textCandidate);
    if (!text) return null;

    let sender =
      msg.sender ||
      msg.from ||
      (msg.type === "system" ? "system" : null);

    if (sender === "agent") sender = "expert";
    if (sender === "customer") sender = "user";
    if (!sender) sender = msg.type === "system" ? "system" : "expert";

    return { sender, text, ...extra };
  };

  useEffect(() => {
    if (!conversationId) return;
    let isMounted = true;

    const fetchMessageHistory = async () => {
      setIsLoadingHistory(true);
      // Add loading message
      setMessages((prev) => {
        const nonHistoryMessages = prev.filter((msg) => !msg._fromHistory && !msg._isLoading);
        return [...nonHistoryMessages, { sender: "system", text: "Loading chat history..", _isLoading: true }];
      });

      try {
        const history = await fetchExpertChatHistory(conversationId);

        const formattedHistory = history.flatMap((item) => {
          // Only show pairs where both user_text and expert_text are not null
          if (item?.user_text && item?.expert_text) {
            const userMessage = pickDisplayText(item.user_text);
            const expertMessage = pickDisplayText(item.expert_text);
            const pairs = [];
            if (userMessage) {
              pairs.push({ sender: "user", text: userMessage, _fromHistory: true });
            }
            if (expertMessage) {
              pairs.push({ sender: "expert", text: expertMessage, _fromHistory: true });
            }
            return pairs;
          }
          // Skip items where either user_text or expert_text is null
          return [];
        });

        if (isMounted) {
          // Remove loading message and add history
          setMessages((prev) => {
            const nonHistoryMessages = prev.filter((msg) => !msg._fromHistory && !msg._isLoading);
            return formattedHistory.length ? [...formattedHistory, ...nonHistoryMessages] : nonHistoryMessages;
          });
        }
      } catch (error) {
        console.error("Failed to fetch expert chat history:", error);
        if (isMounted) {
          // Remove loading message and add error message
          setMessages((prev) => {
            const nonHistoryMessages = prev.filter((msg) => !msg._fromHistory && !msg._isLoading);
            return [
              ...nonHistoryMessages,
              { sender: "system", text: "No previous messages available." },
            ];
          });
        }
      } finally {
        if (isMounted) {
          setIsLoadingHistory(false);
        }
      }
    };

    fetchMessageHistory();

    return () => {
      isMounted = false;
    };
  }, [conversationId]);

  useEffect(() => {
    if (!email) return;

    // Reset refs when email changes or component remounts
    ignoreInitialMessagesRef.current = true;
    connectionTimeRef.current = null;
    initialMessageCountRef.current = 0;

    const wsUrl = `wss://autofiyai.com/api/zendesk/ws/expert-chat/${encodeURIComponent(email)}`;
    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      console.log("✅ WebSocket connected");
      setConnected(true);
      ignoreInitialMessagesRef.current = true;
      connectionTimeRef.current = Date.now();
      initialMessageCountRef.current = 0;
      
      // Clear any existing timeout
      if (initialMessageTimeoutRef.current) {
        clearTimeout(initialMessageTimeoutRef.current);
      }
      
      // Ignore messages for 10 seconds after connection OR first 5 messages
      initialMessageTimeoutRef.current = setTimeout(() => {
        ignoreInitialMessagesRef.current = false;
        console.log("✅ Now accepting WebSocket messages");
      }, 10000);
      
      setMessages((prev) => [ 
        ...prev,
        { sender: "system", text: "Connected to automotive expert. You can start chatting!" },
      ]);
    };
   
    ws.onmessage = (event) => {
      console.log("📩 Received:", event.data);
      
      // Ignore initial messages right after connection
      if (ignoreInitialMessagesRef.current) {
        initialMessageCountRef.current += 1;
        const timeSinceConnection = connectionTimeRef.current 
          ? Date.now() - connectionTimeRef.current 
          : 0;
        
        console.log(`⏭️ Ignoring initial WebSocket message #${initialMessageCountRef.current} (${timeSinceConnection}ms after connection)`);
        
        // Stop ignoring after 10 seconds OR after 5 initial messages
        if (timeSinceConnection > 10000 || initialMessageCountRef.current >= 5) {
          ignoreInitialMessagesRef.current = false;
          if (initialMessageTimeoutRef.current) {
            clearTimeout(initialMessageTimeoutRef.current);
          }
          console.log("✅ Now accepting WebSocket messages");
        }
        return;
      }
      
      try {
        const msg = JSON.parse(event.data);
        const formattedMsg = normalizeChatMessage(msg);

        if (formattedMsg) {
          setMessages((prev) => [...prev, formattedMsg]);
        }
      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };

    ws.onclose = () => {
      console.log("🔌 WebSocket disconnected");
      setConnected(false);
      setMessages((prev) => [
        ...prev,
        { sender: "system", text: "Disconnected from expert chat." },
      ]);
    };

    ws.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
      setConnected(false);
    };

    return () => {
      ws.close();
      if (initialMessageTimeoutRef.current) {
        clearTimeout(initialMessageTimeoutRef.current);
      }
      ignoreInitialMessagesRef.current = true;
      connectionTimeRef.current = null;
      initialMessageCountRef.current = 0;
    };
  }, [email]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Stop ignoring initial messages once user sends their first message
    if (ignoreInitialMessagesRef.current) {
      ignoreInitialMessagesRef.current = false;
      if (initialMessageTimeoutRef.current) {
        clearTimeout(initialMessageTimeoutRef.current);
      }
    }

    // Display user message immediately
    const msg = input.trim();
    setMessages((prev) => [...prev, { sender: "user", text: msg }]);
    setInput("");

    try {
      const result = await sendExpertChatMessage({
        email,
        message: msg,
        vehicleId: vehicle_id || null,
        conversationId: conversationId || null,
      });
      console.log("✅ Message sent successfully:", result);
      
      // Extract ticket_id from response
      if (result?.details?.ticket_id) {
        setTicketId(result.details.ticket_id);
      } else if (result?.ticket_id) {
        setTicketId(result.ticket_id);
      }
    } catch (error) {
      console.error("❌ Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "system", text: "Network error. Please try again." },
      ]);
    }
  };

  const handleCloseClick = () => {
    if (ticketId) {
      onOpen();
    } else {
      onClose();
    }
  };

  const handleConfirmClose = async () => {
    if (ticketId) {
      try {
        await closeTicket(ticketId);
        console.log("✅ Ticket closed successfully");
        onCloseModal();
        onClose();
      } catch (error) {
        console.error("❌ Error closing ticket:", error);
        onCloseModal();
        // Still close the modal even if API call fails
        onClose();
      }
    } else {
      onCloseModal();
      onClose();
    }
  };

  const handleCancelClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onCloseModal(); // Close the confirmation modal
    onClose(); // Also close the expert chat modal (user doesn't want to close ticket, but wants to close chat)
  };

  // Responsive positioning for mobile vs desktop
  const modalStyles = isMobile 
    ? {
        position: "fixed",
        top: "66%",
        left: "54%",
        transform: "translate(-50%, -50%)",
        width: "90%",
        maxWidth: "400px",
        maxHeight: "80vh",
      }
    : {
        position: "fixed",
        bottom: "120px",
        right: "40px",
        width: "350px",
      };

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isMobile && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.500"
          zIndex={999}
          onClick={onClose}
        />
      )}
      <Box
        {...modalStyles}
        bg="white"
        boxShadow="lg"
        borderRadius="lg"
        p={4}
        zIndex={1000}
        border="2px solid #003366"
        display="flex"
        flexDirection="column"
      >

      <HStack justify="space-between" mb={2} overflow="visible">
        <HStack spacing={2} align="center" overflow="visible" flex={1} minW={0}>
          <Text fontWeight="bold" fontSize={isMobile ? "md" : "md"} noOfLines={1} flexShrink={1}>Chat with Automotive Expert</Text>
          <Tooltip 
            label="This may not be real-time, and can take some time to respond" 
            placement="top"
            hasArrow
            shouldWrapChildren={true}
            maxW="200px"
            openDelay={300}
            closeOnClick={false}
          >
            <Box display="inline-flex" alignItems="center" flexShrink={0}>
              <InfoIcon 
                color="gray.500" 
                cursor="pointer" 
                _hover={{ color: "gray.700" }}
                boxSize={4}
              />
            </Box>
          </Tooltip>
          <Box w="8px" h="8px" borderRadius="full" bg={connected ? "green.500" : "red.500"} />
        </HStack>
        <IconButton
          size="sm"
          variant="ghost"
          onClick={handleCloseClick}
          aria-label="Close"
          icon={<Text fontSize="lg" fontWeight="bold">✕</Text>}
          _hover={{ bg: "gray.100" }}
        />
      </HStack>

      <Box
        flex={1}
        overflowY="auto"
        border="1px solid #eee"
        p={2}
        borderRadius="md"
        mb={3}
        minH={isMobile ? "300px" : "250px"}
        maxH={isMobile ? "50vh" : "400px"}
        sx={{
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#212e89",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#1a2566",
          },
          // Firefox
          scrollbarWidth: "thin",
          scrollbarColor: "#212e89 transparent",
        }}
      >
        <VStack align="stretch" spacing={2}>
          {messages.map((msg, i) => (
            <Box
              key={i}
              alignSelf={
                msg.sender === "user"
                  ? "flex-end"
                  : msg.sender === "expert"
                  ? "flex-start"
                  : "center"
              }
            bg={
              msg.sender === "user"
                ? "blue.500"
                : msg.sender === "expert"
                ? "gray.100"
                : "gray.100"
            }
            color={
              msg.sender === "user"
                ? "white"
                : "black"
            }
            p={2}
            borderRadius="md"
            maxW="80%"
            fontSize="sm"
          >
            {msg.text}
            </Box>
          ))}
        </VStack>
      </Box>

      <HStack>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question or message here"
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />
        <IconButton
          icon={<Image src={send_icon} alt="Send" w="6" h="6" />}
          variant="ghost"
          size="md"
          colorScheme="blue"
          aria-label="Send message"
          onClick={sendMessage}
          isDisabled={!input.trim()}
        />
      </HStack>
      </Box>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={handleCancelClose} isCentered closeOnOverlayClick={true} closeOnEsc={true}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Close Ticket</ModalHeader>
          <ModalBody>
            <Text>Do you want to close this ticket?</Text>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={(e) => {
                e.stopPropagation();
                handleCancelClose(e);
              }}
            >
              No
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmClose();
              }}
            >
              Yes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExpertChatBot;
